import React from 'react';
import {
  Box, Typography, Dialog, DialogContent, DialogActions, DialogTitle,
  Divider, Grid, Chip, IconButton, Button, Paper,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Close as CloseIcon, OpenInNew as OpenInNewIcon,
  CalendarToday as CalendarIcon, Person as PersonIcon,
  Category as CategoryIcon, Cloud as CloudIcon,
  Store as StoreIcon, Email as EmailIcon,
} from '@mui/icons-material';
import { SEARCE_COLORS } from '../../theme';
import type { AssetResponse } from '../../types/assets';
import RichTextView from '../common/RichTextView';
import { formatDate, trimText, toHref, getTextLengthFromHtml } from '../../utils/assetHelpers';

// ---------------------------------------------------------------------------
// Small presentational sub-components
// ---------------------------------------------------------------------------

const DetailLabel: React.FC<{ icon: React.ReactElement; label: string; primaryLabel?: boolean }> = ({
  icon,
  label,
  primaryLabel,
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
    {React.cloneElement(icon, { sx: { fontSize: 18, color: SEARCE_COLORS.primary } })}
    <Typography
      variant="caption"
      fontWeight={600}
      sx={{ color: primaryLabel ? SEARCE_COLORS.primary : 'text.secondary' }}
    >
      {label}
    </Typography>
  </Box>
);

const DetailSectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    fontWeight={600}
    sx={{ mb: 0.5, display: 'block', color: SEARCE_COLORS.primary }}
  >
    {children}
  </Typography>
);

const LongTextBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: '#f9fafb', overflow: 'hidden' }}>
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
      {children}
    </Typography>
  </Paper>
);

const ExternalLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <Typography
    variant="body2"
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      color: SEARCE_COLORS.primary,
      textDecoration: 'none',
      '&:hover': { textDecoration: 'underline' },
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
    }}
  >
    {children} <OpenInNewIcon sx={{ fontSize: 14 }} />
  </Typography>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface AssetDetailModalProps {
  asset: AssetResponse | null;
  onClose: () => void;
}

const cloudCellSx = {
  p: 1.5,
  pl: 0,
  pt: 0.5,
  minHeight: 56,
  display: 'flex',
  flexWrap: 'wrap' as const,
  gap: 0.75,
  alignContent: 'flex-start',
  alignItems: 'flex-start',
  wordBreak: 'break-word' as const,
  overflowWrap: 'break-word' as const,
};

const cloudChipSx = {
  height: 'auto',
  py: 0.5,
  '& .MuiChip-label': {
    whiteSpace: 'normal',
    wordBreak: 'break-word' as const,
    overflowWrap: 'anywhere' as const,
    textAlign: 'left' as const,
    lineHeight: 1.35,
  },
};

const AssetDetailModal: React.FC<AssetDetailModalProps> = ({ asset, onClose }) => (
  <Dialog
    open={!!asset}
    onClose={onClose}
    maxWidth="xl"
    fullWidth
    scroll="paper"
    PaperProps={{
      sx: {
        borderRadius: '16px',
        width: '100%',
        maxWidth: { xs: '100%', sm: 'min(1400px, calc(100vw - 48px))' },
      },
    }}
  >
    {asset && (
      <>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, pb: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
              {asset.asset_code ?? '—'}
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ pr: 1 }}>{asset.asset_name}</Typography>
            {asset.short_description?.trim() ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, pr: 1 }}>
                {asset.short_description.trim()}
              </Typography>
            ) : null}
          </Box>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={3}>
              <DetailLabel icon={<CategoryIcon />} label="Asset Type" />
              <Typography variant="body2">{asset.asset_type || '—'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DetailLabel icon={<StoreIcon />} label="GTM Category" />
              <Typography variant="body2">{asset.gtm_category || '—'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DetailLabel icon={<CalendarIcon />} label="Approx. Start Date" />
              <Typography variant="body2">
                {asset.approx_start_date ? formatDate(asset.approx_start_date) : '—'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DetailLabel icon={<CalendarIcon />} label="Completion Date" />
              <Typography variant="body2">{formatDate(asset.asset_completion_date)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DetailLabel icon={<CalendarIcon />} label="Submitted At" />
              <Typography variant="body2">{formatDate(asset.submitted_at)}</Typography>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <DetailLabel icon={<PersonIcon />} label="Submitted By" />
              <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                {asset.submitted_by}
              </Typography>
            </Grid>

            {/* Key Cloud Services: same column as Approx. Start Date (table-like cell, wraps) */}
            <Grid item xs={12} sm={6} md={3}>
              <DetailLabel icon={<CloudIcon />} label="Key Cloud Services" primaryLabel />
              <Box component="div" role="group" aria-label="Key cloud services" sx={cloudCellSx}>
                {asset.key_cloud_services.length > 0 ? (
                  asset.key_cloud_services.map((s, i) => (
                    <Chip key={i} label={s} size="small" sx={cloudChipSx} />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item md={3} sx={{ display: { xs: 'none', md: 'block' } }} aria-hidden />

            <Grid item xs={12}><Divider /></Grid>

            <Grid item xs={12}>
              <DetailSectionLabel>Problem Solved / Core Purpose</DetailSectionLabel>
              {getTextLengthFromHtml(asset.problem_solved) > 0 ? (
                <Paper variant="outlined" sx={{ borderRadius: 2, backgroundColor: '#f9fafb', overflow: 'hidden' }}>
                  <RichTextView html={asset.problem_solved} />
                </Paper>
              ) : (
                <LongTextBox>—</LongTextBox>
              )}
            </Grid>

            <Grid item xs={12}>
              <DetailSectionLabel>Primary Value Proposition</DetailSectionLabel>
              {getTextLengthFromHtml(asset.primary_value_prop) > 0 ? (
                <Paper variant="outlined" sx={{ borderRadius: 2, backgroundColor: '#f9fafb', overflow: 'hidden' }}>
                  <RichTextView html={asset.primary_value_prop} />
                </Paper>
              ) : (
                <LongTextBox>—</LongTextBox>
              )}
            </Grid>

            <Grid item xs={12}>
              <DetailSectionLabel>Target Customers / Industries</DetailSectionLabel>
              <Typography variant="body2">{asset.target_customers_industries}</Typography>
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            <Grid item xs={12}>
              <DetailSectionLabel>Internal Documentation</DetailSectionLabel>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                {asset.internal_documentation.map((doc, i) => {
                  const href = toHref(doc.url);
                  const label = doc.category?.trim() || 'Document';
                  return (
                    <Chip
                      key={`${href}-${i}`}
                      component="a"
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      clickable
                      label={label}
                      size="small"
                      variant="outlined"
                      icon={<OpenInNewIcon sx={{ fontSize: '1rem !important' }} />}
                      title={`${label} — open link`}
                      sx={{
                        maxWidth: 280,
                        borderColor: SEARCE_COLORS.primary,
                        color: SEARCE_COLORS.primary,
                        textDecoration: 'none',
                        '&:hover': {
                          backgroundColor: alpha(SEARCE_COLORS.primary, 0.08),
                          borderColor: SEARCE_COLORS.primary,
                        },
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </Grid>

            {asset.external_documentation && asset.external_documentation.length > 0 && (
              <Grid item xs={12}>
                <DetailSectionLabel>External Documentation</DetailSectionLabel>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                  {asset.external_documentation.map((doc, i) => {
                    const href = toHref(typeof doc === 'string' ? doc : doc.url);
                    const label = (typeof doc === 'string' ? doc : doc.name?.trim()) || 'Link';
                    return (
                      <Chip
                        key={`${href}-${i}`}
                        component="a"
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        clickable
                        label={label}
                        size="small"
                        variant="outlined"
                        icon={<OpenInNewIcon sx={{ fontSize: '1rem !important' }} />}
                        title={`${label} — ${href}`}
                        sx={{
                          maxWidth: 280,
                          borderColor: SEARCE_COLORS.primary,
                          color: SEARCE_COLORS.primary,
                          textDecoration: 'none',
                          '&:hover': {
                            backgroundColor: alpha(SEARCE_COLORS.primary, 0.08),
                            borderColor: SEARCE_COLORS.primary,
                          },
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                        }}
                      />
                    );
                  })}
                </Box>
              </Grid>
            )}

            <Grid item xs={12}><Divider /></Grid>

            <Grid item xs={12} md={3}>
              <DetailSectionLabel>Marketplace</DetailSectionLabel>
              <Chip
                label={asset.marketplace_listing_status ? 'Listed' : 'Not Listed'}
                size="small"
                color={asset.marketplace_listing_status ? 'success' : 'default'}
              />
              {asset.marketplace_listing_status && asset.marketplace_url && (
                <Box sx={{ mt: 0.5 }}>
                  <ExternalLink href={toHref(asset.marketplace_url)}>
                    {trimText(asset.marketplace_url, 30)}
                  </ExternalLink>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              <DetailSectionLabel>Pricing Model</DetailSectionLabel>
              <Typography variant="body2">{asset.pricing_model || '—'}</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <DetailSectionLabel>Gitlab Repo</DetailSectionLabel>
              {asset.gitlab_repo_url ? (
                <ExternalLink href={toHref(asset.gitlab_repo_url)}>View Repo</ExternalLink>
              ) : (
                <Typography variant="body2">—</Typography>
              )}
            </Grid>
            <Grid item xs={12} md={3}>
              <DetailSectionLabel>PR Code (SFDC)</DetailSectionLabel>
              <Typography variant="body2">{asset.pr_code || '—'}</Typography>
            </Grid>

            <Grid item xs={12}>
              <DetailLabel icon={<EmailIcon />} label="Team Members" />
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {asset.team_members.map((email, i) => (
                  <Chip key={i} label={email} size="small" icon={<PersonIcon />} />
                ))}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} variant="outlined">Close</Button>
        </DialogActions>
      </>
    )}
  </Dialog>
);

export default AssetDetailModal;
